import { type Result, ok, err, deepEquals, stableJSONStringify } from '@conduit-client/utils';

import { ArrayIsArray } from '@conduit-client/utils';
import type { NamedPubSubService } from '@conduit-client/service-pubsub/v1';
import type {
    InstrumentationAttributes,
    NamedInstrumentationService,
} from '@conduit-client/service-instrumentation/v1';
import type { NamedCacheControllerService } from '@conduit-client/service-cache-control/v1';
import type {
    Key,
    CanonicalCacheControlMetadata,
    ReadonlyCache,
    CacheEntry,
    Cache,
} from '@conduit-client/service-cache/v1';

export interface NormalizedLink {
    type: 'link';
    linkedKey: Key;
}

type UnknownError = {
    type: 'unknown';
    error: Error;
};

/**
 * An error that occurs when a type tries to read from the cache, and the cache metadata does
 * not match the current type
 */
type IncorrectTypeError = {
    type: 'incorrectType';
    expectedType: string;
    actualType: string;
};

/**
 * An error that occurs when a type tries to read from the cache and data is missing
 */
type MissingDataError = {
    type: 'missingData';
    namespace: string;
    typeName: string;
    data: unknown;
};

/**
 * A union of errors that can be returned during the write process
 */
export type WriteErrors = Array<UnknownError>;

/**
 * A union of errors that can be returned during the read process
 */
export type ReadErrors = Array<UnknownError | IncorrectTypeError | MissingDataError>;

export type ReadResult<Data> = Result<Data, ReadErrors>;
export type WriteResult<Data> = Result<Data, WriteErrors>;

/**
 * A Type defines a collection of functions associated with a particular
 * data type. Types are not normalized but may contain normalized data.
 *
 * @typeParam Data TypeScript type that corresponds to the data
 * @typeParam NormalizedData TypeScript type that corresponds to the normalized data
 * @typeParam NormalizeInputData TypeScript type that corresponds to the data that the normalize method expects as input. Defaults to the same as Data
 */
export type TypeRepository<Data, ReadInput, WriteResponse, WriteInput> = {
    readonly namespace: string;
    readonly typeName: string;

    equals(x: Data, y: Data): boolean;

    /**
     * Normalizes data for storage.
     */
    write(cache: Cache, input: WriteInput): WriteResult<WriteResponse>;

    /**
     * De-normalizes a Type's data. A type could normalize into a Link, or return Data to the caller.
     * A caller must not be relying on the referenced type being normalized or not.
     * If denormalization fails, it will return undefined.
     */
    read(cache: ReadonlyCache, input: ReadInput): ReadResult<Data>;
};

/**
 * An InvalidatableType defines an invalidate function that takes a generic input,
 * and returns a promise that resolves when the invalidation is complete
 *
 * @typeParam T is another type to extend
 * @typeParam InvalidateInput is the input to run an invalidation
 */
export type Invalidatable<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- constraint generic positions must be `any`; `unknown` breaks conditional type matching for concrete TypeRepository instantiations
    T extends TypeRepository<any, any, any, any>,
    InvalidateInput,
> = T & {
    /**
     *
     * @param configs Partial KeyConfigs for this Type (partial KeyConfig treats missing parts as wildcard)
     */
    invalidate(configs: InvalidateInput[]): Promise<void>;
};

/**
 * A QueryableType defines a collection of functions associated with a particular
 * data type that gets its own cache entry.
 *
 * @typeParam Data TypeScript type that corresponds to the data
 * @typeParam KeyConfig TypeScript type for the inputs needed to construct
 *   a Key for the data
 * @typeParam NormalizeInputData TypeScript type that corresponds to the data that the normalize method expects as input. Defaults to the same as Data
 */
export type Queryable<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- constraint generic positions must be `any`; `unknown` breaks conditional type matching for concrete TypeRepository instantiations
    T extends TypeRepository<any, any, any, any>,
    QueryInput,
> = T & {
    /**
     *
     * @param cache The cache to run the query against
     * @param query The query to run
     */
    query(cache: Cache, query: QueryInput): ReadResult<DataOf<T>>;
};

/* eslint-disable @typescript-eslint/no-explicit-any -- extractor type non-inferred generic positions must be `any`; `unknown` breaks conditional match so concrete repositories collapse to never */
export type DataOf<T extends TypeRepository<any, any, any, any>> =
    T extends TypeRepository<infer Data, any, any, any> ? Data : never;
export type ReadInputOf<T extends TypeRepository<any, any, any, any>> =
    T extends TypeRepository<any, infer ReadInput, any, any> ? ReadInput : never;
export type WriteResponseOf<T extends TypeRepository<any, any, any, any>> =
    T extends TypeRepository<any, any, infer WriteResponse, any> ? WriteResponse : never;
export type WriteResultOf<T extends TypeRepository<any, any, any, any>> = WriteResult<
    WriteResponseOf<T>
>;
export type WriteInputOf<T extends TypeRepository<any, any, any, any>> =
    T extends TypeRepository<any, any, any, infer WriteInput> ? WriteInput : never;
export type InvalidateInputOf<T extends TypeRepository<any, any, any, any>> =
    T extends Invalidatable<TypeRepository<any, any, any, any>, infer InvalidateInput>
        ? InvalidateInput
        : never;
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * A utility method for ensuring that a given entry in the cache matches a type based on namespace and name information
 * @param cacheEntry the cache entry to validate against
 * @param type the type to validate the cache entry against
 * @returns
 */
export function isCacheEntryForType(
    cacheEntry: CacheEntry<unknown>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- function parameter constraint must be `any` to accept any concrete TypeRepository; `unknown` is too restrictive
    type: TypeRepository<any, any, any, any>
): boolean {
    return (
        cacheEntry.metadata.type.namespace === type.namespace &&
        cacheEntry.metadata.type.name === type.typeName
    );
}

/* eslint-disable @typescript-eslint/no-explicit-any -- extractor type non-inferred generic positions must be `any`; `unknown` breaks conditional match so concrete repositories collapse to never */
export type KeyParamsOf<T> =
    T extends IdentifiableTypeRepository<
        any,
        any,
        infer KeyParams,
        Record<string, unknown>,
        Record<string, unknown>
    >
        ? KeyParams
        : never;
/* eslint-enable @typescript-eslint/no-explicit-any */

export type WriteInput<Data, Extension = Record<string, unknown>> = { data: Data } & Extension;
export type NormalizeDataInput<
    Data,
    NormalizedData,
    Extension extends Record<string, unknown> = Record<string, unknown>,
> = WriteInput<Data, Extension & { existingNormalizedData?: NormalizedData }>;

export type ReadInput<Extension extends Record<string, unknown> = Record<string, unknown>> = {
    normalizedData: NormalizedLink;
} & Extension;
export type ReadByKeyParamsInput<KeyParams> = {
    keyParams: KeyParams;
};
export type DenormalizeInput<Extension extends Record<string, unknown> = Record<string, unknown>> =
    Extension & {
        key: Key;
    };
export type DenormalizeDataInput<
    NormalizedData,
    Extension extends Record<string, unknown> = Record<string, unknown>,
> = Extension & { normalizedData: NormalizedData };

export type KeyParamQuery<KeyParams, ReadInputExtension> = {
    type: 'keyParams';
    keyParams: KeyParams;
} & ReadInputExtension;
/* eslint-disable @typescript-eslint/no-explicit-any -- extractor type non-inferred generic positions must be `any`; `unknown` breaks conditional match so concrete repositories collapse to never */
export type QueryFor<T> =
    T extends Queryable<TypeRepository<any, any, any, any>, infer Query> ? Query : never;
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * An abstract base class that implements Type, defining additional abstract properties and methods
 * for building a key for a given set of parameters, and defining the canonical cache control metadata
 * for any data written
 */
export abstract class IdentifiableTypeRepository<
    Data,
    NormalizedData,
    KeyParams,
    ReadInputExtension extends Record<string, unknown> = Record<string, unknown>,
    WriteInputExtension extends Record<string, unknown> = Record<string, unknown>,
    Query extends KeyParamQuery<KeyParams, ReadInputExtension> = KeyParamQuery<
        KeyParams,
        ReadInputExtension
    >,
> implements
        Queryable<
            TypeRepository<
                Data,
                ReadInput<ReadInputExtension>,
                NormalizedLink,
                WriteInput<Data, WriteInputExtension>
            >,
            Query
        >
{
    constructor(protected services: {}) {}
    abstract readonly cacheControl: CanonicalCacheControlMetadata;
    abstract readonly namespace: string;
    abstract readonly typeName: string;
    protected abstract denormalizeData(
        cache: ReadonlyCache,
        input: DenormalizeDataInput<NormalizedData, ReadInput<ReadInputExtension>>
    ): Result<Data, ReadErrors>;
    protected abstract normalizeData(
        cache: Cache,
        input: NormalizeDataInput<
            Data,
            NormalizedData,
            WriteInputExtension & { existingNormalizedData?: NormalizedData }
        >
    ): Result<NormalizedData, WriteErrors>;
    get cacheMetadata() {
        return {
            cacheControl: { ...this.cacheControl },
            type: { namespace: this.namespace, name: this.typeName },
        };
    }

    buildKey(params: KeyParams): Key {
        return `${this.namespace}::${this.typeName}(${stableJSONStringify(params)})`;
    }
    abstract buildKeyParams(input: WriteInput<Data, WriteInputExtension>): KeyParams;

    write(cache: Cache, input: WriteInput<Data, WriteInputExtension>): WriteResult<NormalizedLink> {
        // Identifiable types can have a key that is not the same as the input normalized data
        // so we need to build the key from the input, and then get the existing normalized data from the cache
        // and pass it to the normalizeData method. This should ALWAYS be done for identifiable types.
        const key = this.buildKey(this.buildKeyParams(input));
        const existingNormalizedData: NormalizedData | undefined = cache.get<NormalizedData>(key)
            ?.value as NormalizedData | undefined;
        const normalized = this.normalizeData(cache, { ...input, existingNormalizedData });
        if (normalized.isErr()) {
            return err(normalized.error);
        }

        cache.set<NormalizedData>(key, {
            value: normalized.value,
            metadata: this.cacheMetadata,
        });

        return ok({
            type: 'link',
            linkedKey: key,
        } as const);
    }

    protected validateEntry(entry: CacheEntry<unknown>): Result<undefined, ReadErrors> {
        if (!isCacheEntryForType(entry, this)) {
            return err([
                {
                    type: 'incorrectType',
                    expectedType: this.typeName,
                    actualType: entry.metadata.type.name,
                },
            ]);
        }
        return ok(undefined);
    }

    query(cache: Cache, query: Query): ReadResult<Data> {
        return this.read(cache, {
            ...query,
            normalizedData: { type: 'link', linkedKey: this.buildKey(query.keyParams) },
        });
    }

    read(cache: ReadonlyCache, input: ReadInput<ReadInputExtension>): Result<Data, ReadErrors> {
        return this.denormalize(cache, { ...input, key: input.normalizedData.linkedKey });
    }

    denormalize(cache: ReadonlyCache, input: DenormalizeInput<ReadInput<ReadInputExtension>>) {
        const cacheEntry = cache.get<NormalizedData>(input.key, {
            copy: false,
        }) as CacheEntry<NormalizedData>;

        if (cacheEntry) {
            const validationResult = this.validateEntry(cacheEntry);
            if (validationResult.isErr()) {
                return err([...validationResult.error]);
            }
            const normalizedData = cacheEntry.value;
            return this.denormalizeData(cache, { ...input, normalizedData });
        }

        return err([this.buildMissingDataError()]);
    }

    equals(x: Data, y: Data) {
        return deepEquals(x, y);
    }

    buildMissingDataError(): MissingDataError {
        return {
            type: 'missingData',
            typeName: this.typeName,
            namespace: this.namespace,
            data: undefined,
        };
    }
}

/**
 * An abstract base class that extends IdentifiableType, adding the additional constraint that the
 * only data required to write to the cache is an instance of the data itself, i.e. the identity of
 * the data lives inside the data
 */
export abstract class SelfIdentifiableTypeRepository<
    Data,
    NormalizedData,
    KeyParams,
    ReadInputExtension extends Record<string, unknown> = Record<string, unknown>,
    WriteInputExtension extends Record<string, unknown> = Record<string, unknown>,
> extends IdentifiableTypeRepository<
    Data,
    NormalizedData,
    KeyParams,
    ReadInputExtension,
    WriteInputExtension
> {}

/**
 * Adds flat positional keying (keySchema), keySchema-based buildKey, and invalidate/evict
 * to IdentifiableTypeRepository. Key params are supplied externally (e.g. from request
 * config) — this class makes NO claim that identity lives in the data. KeyParams must be a
 * flat object (scalars, scalar arrays, or stable-stringifiable objects). Provides
 * invalidation + key building 'for free'.
 */
export abstract class FlattenedKeyIdentifiableTypeRepository<
        Data,
        NormalizedData,
        KeyParams extends FlattenedKeyParams,
        ReadInputExtension extends Record<string, unknown> = Record<string, unknown>,
        WriteInputExtension extends Record<string, unknown> = Record<string, unknown>,
    >
    extends IdentifiableTypeRepository<
        Data,
        NormalizedData,
        KeyParams,
        ReadInputExtension,
        WriteInputExtension
    >
    implements
        Invalidatable<
            IdentifiableTypeRepository<
                Data,
                NormalizedData,
                KeyParams,
                ReadInputExtension,
                WriteInputExtension
            >,
            Partial<KeyParams>
        >
{
    instrumentationAttributes: InstrumentationAttributes | undefined;
    constructor(
        protected services: NamedCacheControllerService &
            NamedPubSubService &
            Partial<NamedInstrumentationService>
    ) {
        super(services);
    }

    abstract readonly keySchema: string[];

    async invalidate(configs: Partial<KeyParams>[]): Promise<void> {
        return this.setupCacheOperation(configs, 'invalidate', 'cacheInvalidation');
    }

    protected collectInstrumentation(duration: number, type: 'invalidate' | 'evict') {
        if (this.services.instrumentation) {
            const meter = this.services.instrumentation.metrics.getMeter('onestore');
            const attributes = {
                ...this.instrumentationAttributes,
                namespace: this.namespace,
                typeName: this.typeName,
            };
            meter.createCounter<InstrumentationAttributes>(`type.${type}.count`).add(1, attributes);
            meter
                .createHistogram<InstrumentationAttributes>(`type.${type}.duration`)
                .record(duration, attributes);
        }
    }

    async evict(configs: Partial<KeyParams>[]): Promise<void> {
        return this.setupCacheOperation(configs, 'evict', 'cacheEviction');
    }

    private async setupCacheOperation(
        configs: Partial<KeyParams>[],
        cacheUpdateType: 'invalidate' | 'evict',
        eventType: 'cacheInvalidation' | 'cacheEviction'
    ) {
        const startTime = this.services.instrumentation
            ? this.services.instrumentation.currentTimeMs()
            : 0;
        const result = this.runCacheOperation(configs, cacheUpdateType, eventType).then(() => {
            if (this.services.instrumentation) {
                const duration = this.services.instrumentation.currentTimeMs() - startTime;
                this.collectInstrumentation(duration, cacheUpdateType);
            }
        });
        return result;
    }

    /**
     * Shared implementation for cache operations (invalidate/evict)
     * @param configs - Key configurations to match
     * @param cacheUpdateType - Type of cache update to perform
     * @param eventType - Type of event to publish
     */
    private async runCacheOperation(
        configs: Partial<KeyParams>[],
        cacheUpdateType: 'invalidate' | 'evict',
        eventType: 'cacheInvalidation' | 'cacheEviction'
    ): Promise<void> {
        const regex = buildRegexForNamespacedKeys(
            this.namespace,
            this.typeName,
            configs,
            this.keySchema
        );

        const results: Set<Key> = new Set();
        for await (const entry of this.services.cacheController.findAndModify(
            { key: { $regex: regex } },
            { type: cacheUpdateType }
        )) {
            results.add(entry);
        }
        if (results.size > 0) {
            await this.services.pubSub.publish({
                type: eventType,
                data: results,
            });
        }
    }

    buildKey(config: KeyParams): Key {
        return buildNamespacedTypeKey(this.namespace, this.typeName, config, this.keySchema);
    }
}

/**
 * Flat-keyed identifiable type whose identity lives inside the data itself
 * (self-identifiable). Behaviorally identical to FlattenedKeyIdentifiableTypeRepository;
 * the distinction is a semantic marker for generators that derive keyParams from data.
 */
export abstract class FlattenedKeySelfIdentifiableTypeRepository<
    Data,
    NormalizedData,
    KeyParams extends FlattenedKeyParams,
    ReadInputExtension extends Record<string, unknown> = Record<string, unknown>,
    WriteInputExtension extends Record<string, unknown> = Record<string, unknown>,
> extends FlattenedKeyIdentifiableTypeRepository<
    Data,
    NormalizedData,
    KeyParams,
    ReadInputExtension,
    WriteInputExtension
> {}
type ScalarKeyValue = string | number | boolean | null | undefined;
type FlattenedKeyParams = Record<string, ScalarKeyValue | ScalarKeyValue[] | object>;
export function buildNamespacedTypeKey(
    namespace: string,
    typeName: string,
    keyConfig: FlattenedKeyParams,
    keySchema: string[]
): string {
    const keyParts = keySchema.map((key) => serializeKeyValue(keyConfig[key])).join('|');
    return `${namespace}::${typeName}(${keyParts})`;
}

// A scalar (or array of scalars) is serialized inline; any other value (object,
// array-of-objects) is stable-stringified into a single opaque key slot so that
// resource/wrapper keys built from arbitrary request config remain deterministic
// and regex-matchable. Sub-object wildcarding is intentionally unsupported.
function serializeKeyValue(value: unknown): string {
    if (ArrayIsArray(value)) {
        const allScalar = value.every(
            (v) => v === null || v === undefined || typeof v !== 'object'
        );
        return encodeKeySlot(allScalar ? value.join() : (stableJSONStringify(value) ?? ''));
    }
    if (value !== null && value !== undefined && typeof value === 'object') {
        return encodeKeySlot(stableJSONStringify(value) ?? '');
    }
    return encodeKeySlot(`${value}`);
}

// Slots are joined with a literal `|`, and the wildcard regex treats `|` as a slot
// boundary (`[^|]*?` / `\|`). A raw `|` inside a serialized value would shift those
// boundaries, so wildcard invalidation would silently miss the entry. Percent-encode
// `%` and `|` so a slot value can never contain a raw delimiter. The encoding is
// injective and applied identically on the key-build and regex-build paths (both go
// through serializeKeyValue), so equivalent configs still produce identical keys.
function encodeKeySlot(serialized: string): string {
    return serialized.replace(/%/g, '%25').replace(/\|/g, '%7C');
}

export function buildRegexForNamespacedKeys(
    namespace: string,
    typeName: string,
    partialKeyConfigs: Array<FlattenedKeyParams>,
    keySchema: string[]
): RegExp {
    const patterns = partialKeyConfigs.map((partial) => {
        // Build a regex pattern for a single KeyConfig
        const requiredParts = keySchema
            .map((key) => {
                if (!(key in partial)) {
                    return `[^|]*?`; // Allow any value or absence of value (optional match)
                }
                const value = partial[key];
                const serialized = serializeKeyValue(value);
                return `(?:${serialized.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`;
            })
            .join('\\|'); // Ensure properties are matched in order with literal separator

        return `^${namespace}::${typeName}\\((?:${requiredParts})\\)$`;
    });

    return new RegExp(`^(?:${patterns.join('|')})$`); // Apply OR logic across separate partial configs
}

export function extractReadWriteData<Data, Error>(
    result: Result<Data, Error[]>,
    errorCollector: Error[]
): Data | undefined {
    if (result.isOk()) {
        return result.value;
    }

    errorCollector.push(...result.error);
    return undefined;
}

export function buildReadWriteResult<Data, Errors extends WriteErrors | ReadErrors>(
    data: unknown,
    errors: Errors
): Result<Data, Errors> {
    if (errors.length > 0) {
        return err(errors);
    }

    return ok(data as Data);
}

// This type is the normalized representation of union data (either a pure union, or a discriminated union)
// This data shape will live in the cache, and contains the discriminator (i.e. an identity of what type of data this entry represents)
// and the normalized data for that entry
export type UnionRepresentation<Discriminator extends string, Data> = {
    discriminator: Discriminator;
    data: Data;
};

// A utility type that represents all the information required for normalizing and
// denormalizing types into a cache, including the discriminator value, the
// canonical representation (Data) and the normalized representation (Normalized)
export type DiscriminatorMap<Discriminator extends string, Data, Normalized> = {
    [k in Discriminator]: {
        data: Data;
        normalized: Normalized;
    };
};

// A utility type for inferring the union of normalized representations
// possible based on a given DiscriminatorType
export type NormalizedRepresentationOf<M extends DiscriminatorMap<string, unknown, unknown>> =
    M extends DiscriminatorMap<infer D, unknown, unknown>
        ? { [k in D]: UnionRepresentation<k, M[k]['normalized']> }[D]
        : never;

// A utility type for inferring the discriminator -> denormalize function map
// required by the `denormalizeUnionRepresentation` function
export type DenormalizeMapOf<M extends DiscriminatorMap<string, unknown, unknown>> =
    M extends DiscriminatorMap<infer D, unknown, unknown>
        ? { [k in D]: (normalized: M[k]['normalized']) => M[k]['data'] }
        : never;

// A utility type for inferring the canonical data type union based on
// a DiscriminatorMap type
export type UnionDataOf<M extends DiscriminatorMap<string, unknown, unknown>> =
    M extends DiscriminatorMap<string, infer D, unknown> ? D : never;

// A utility type for inferring the discriminator string union from a
// given discriminator map type
export type DiscriminatorOf<M extends DiscriminatorMap<string, unknown, unknown>> =
    M extends DiscriminatorMap<infer D, unknown, unknown> ? D : never;

// A utilty type for inferring the test/normalize map type from a
// discriminator map type to be used in the `normalizeUnionRepresentation` function
export type DiscriminatorNormalizeFunctionsMap<
    M extends DiscriminatorMap<string, unknown, unknown>,
> =
    M extends DiscriminatorMap<infer D, unknown, unknown>
        ? {
              [k in D]: {
                  test: (data: M[D]['data']) => boolean | Result<boolean, Error[]>;
                  normalize: (data: M[k]['data']) => M[k]['normalized'];
              };
          }
        : never;

/**
 *
 * @param normalized The normalized data representation, including the discriminator value
 * @param denormalizeMap A map of discriminator -> denormalize functions to be used based on the datas discriminator
 * @returns
 */
export function denormalizeUnionRepresentation<
    M extends DiscriminatorMap<string, unknown, unknown>,
>(normalized: NormalizedRepresentationOf<M>, denormalizeMap: DenormalizeMapOf<M>): UnionDataOf<M> {
    const discriminator = normalized.discriminator as DiscriminatorOf<M>;
    return denormalizeMap[discriminator](normalized.data) as UnionDataOf<M>;
}

/**
 *
 * @param data The canonical/denormalized data representation
 * @param normalizeTestMap A map of discriminator to test method which return a boolean indicating if the data adheres to this "discriminator"
 * and a normalize function to call if the data matches this discriminator
 * @returns
 */
export function normalizeUnionRepresentation<M extends DiscriminatorMap<string, unknown, unknown>>(
    data: UnionDataOf<M>,
    normalizeTestMap: DiscriminatorNormalizeFunctionsMap<M>
): NormalizedRepresentationOf<M> {
    const perArmFailures: string[] = [];
    for (const discriminator of Object.keys(normalizeTestMap) as DiscriminatorOf<M>[]) {
        const discriminatorLogic = normalizeTestMap[discriminator];
        const outcome = discriminatorLogic.test(data);
        const matched = typeof outcome === 'boolean' ? outcome : outcome.isOk();
        if (matched) {
            return {
                discriminator,
                data: discriminatorLogic.normalize(data),
            } as unknown as NormalizedRepresentationOf<M>;
        }
        if (typeof outcome !== 'boolean' && outcome.isErr()) {
            perArmFailures.push(
                `  [${discriminator}]: ${outcome.error.map((e) => e.message).join('; ')}`
            );
        }
    }

    const candidates = Object.keys(normalizeTestMap);
    const value = stableJSONStringify(data) ?? String(data);
    const perArmDetail =
        perArmFailures.length > 0
            ? `\nPer-arm validation failures:\n${perArmFailures.join('\n')}`
            : '';
    throw new Error(
        `No matching union member found. Value matched none of the ${candidates.length} candidate arms [${candidates.join(', ')}].${perArmDetail}\nOffending value: ${value}`
    );
}
